import { useState, useEffect } from "react";
import { createAssessment, getAssessmentHistory, getOrganizationStats } from "../../service/assessment";
import { useAuth } from "../../context/AuthContext";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { RECOMMENDATIONS } from "./wellnessindex";




// ── Design Tokens matching echoBuddy portal ──
const COLORS = {
  primary: "#2ABFAB",
  primaryDark: "#1EA898",
  primaryLight: "#E6FAF7",
  greenBtn: "#2ABFAB",
  purple: "#6C63FF",
  blue: "#3B7DDD",
  bg: "#F5F7FA",
  card: "#FFFFFF",
  border: "#E2E8F0",
  borderLight: "#EDF2F7",
  textDark: "#1A202C",
  textMid: "#4A5568",
  textLight: "#718096",
  textMuted: "#A0AEC0",
};

const DIMENSIONS = [
  { key: "emotional", label: "Emotional", icon: "💛", color: "#F6AD55", lightBg: "#FFFAF0", borderColor: "#FEEBC8" },
  { key: "mental", label: "Mental", icon: "🧠", color: "#6C63FF", lightBg: "#F0EFFE", borderColor: "#D6D3FF" },
  { key: "physical", label: "Physical", icon: "💪", color: "#2ABFAB", lightBg: "#E6FAF7", borderColor: "#B2F0E6" },
  { key: "social", label: "Social", icon: "🤝", color: "#3B7DDD", lightBg: "#EBF4FF", borderColor: "#BEE3F8" },
  { key: "financial", label: "Financial", icon: "📊", color: "#E53E8C", lightBg: "#FFF0F6", borderColor: "#FED7E8" },
];

const TIERS = [
  { min: 1, max: 20, label: "Critical", color: "#E53E3E", bg: "#FFF5F5", border: "#FED7D7" },
  { min: 21, max: 40, label: "At Risk", color: "#DD6B20", bg: "#FFFAF0", border: "#FEEBC8" },
  { min: 41, max: 60, label: "Developing", color: "#D69E2E", bg: "#FFFFF0", border: "#FEFCBF" },
  { min: 61, max: 80, label: "Healthy", color: "#38A169", bg: "#F0FFF4", border: "#C6F6D5" },
  { min: 81, max: 100, label: "Thriving", color: "#2ABFAB", bg: "#E6FAF7", border: "#B2F0E6" },
];

const TRAFFIC = (s) => s <= 35 ? { l: "Red", c: "#E53E3E" } : s <= 65 ? { l: "Amber", c: "#D69E2E" } : { l: "Green", c: "#38A169" };
const getTier = (s) => TIERS.find(t => s >= t.min && s <= t.max) || TIERS[0];

const QUESTIONS = {
  emotional: [
    "I feel emotionally balanced most days",
    "I can manage my stress effectively",
    "I feel optimistic about my future",
    "I can express my emotions in a healthy way",
  ],
  mental: [
    "I can focus and concentrate at work",
    "I feel mentally sharp and clear-headed",
    "I am able to disconnect from work during off-hours",
    "I feel motivated and engaged in my tasks",
  ],
  physical: [
    "I get adequate sleep (7–9 hours)",
    "I exercise regularly (3+ times per week)",
    "I maintain a balanced diet",
    "I have enough energy throughout the day",
  ],
  social: [
    "I have meaningful connections with colleagues",
    "I feel supported by my manager / team",
    "I have a healthy work-life balance",
    "I feel a sense of belonging at my workplace",
  ],
  financial: [
    "I feel confident about my financial situation",
    "I can manage unexpected expenses",
    "I am on track with my financial goals",
    "Financial stress does not impact my work performance",
  ],
};


const getRecLevel = (s) => {
  if (s <= 20) return "critical";
  if (s <= 40) return "atRisk";
  if (s <= 60) return "developing";
  if (s <= 80) return "healthy";
  return "thriving";
};

function ScoreRing({ score, size = 140, strokeWidth = 10, color, delay = 0 }) {
  const [val, setVal] = useState(0);
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;

  useEffect(() => {
    const t = setTimeout(() => {
      let cur = 0;
      const step = () => {
        cur += 1.5;
        if (cur >= score) { setVal(score); return; }
        setVal(Math.round(cur));
        requestAnimationFrame(step);
      };
      step();
    }, delay);
    return () => clearTimeout(t);
  }, [score, delay]);

  const tier = getTier(val);
  const fill = color || tier.color;

  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} style={{ position: 'absolute', transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDF2F7" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={fill} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={circ - (val / 100) * circ}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.08s ease" }} />
      </svg>
      <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <span style={{ fontSize: size > 120 ? 32 : 20, fontWeight: 700, color: COLORS.textDark }}>{val}</span>
        <span style={{ fontSize: 10, color: COLORS.textMuted }}>/100</span>
      </div>
    </div>
  );
}

function TierBadge({ score, small }) {
  const t = getTier(score);
  return (
    <span style={{
      display: "inline-block", padding: small ? "2px 8px" : "3px 12px",
      borderRadius: 20, fontSize: small ? 11 : 12, fontWeight: 700,
      color: t.color, backgroundColor: t.bg, border: `1px solid ${t.border}`,
    }}>{t.label}</span>
  );
}

function TrafficDot({ score }) {
  const t = TRAFFIC(score);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: t.c, boxShadow: `0 0 8px ${t.c}50`, display: "inline-block" }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: t.c }}>{t.l}</span>
    </span>
  );
}

function ProgressBar({ score, color }) {
  return (
    <div style={{ width: "100%", height: 6, borderRadius: 6, backgroundColor: "#EDF2F7", overflow: "hidden" }}>
      <div style={{ height: "100%", borderRadius: 6, width: `${score}%`, backgroundColor: color, transition: "width 1s ease" }} />
    </div>
  );
}

const cardStyle = {
  backgroundColor: COLORS.card, borderRadius: 16,
  border: `1px solid ${COLORS.border}`, padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const btnPrimary = {
  background: "linear-gradient(135deg, #2ABFAB 0%, #6C63FF 100%)",
  color: "#fff", border: "none", borderRadius: 12,
  padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer",
  boxShadow: "0 2px 8px rgba(42,191,171,0.25)",
};

const btnSecondary = {
  backgroundColor: COLORS.card, color: COLORS.textMid,
  border: `1px solid ${COLORS.border}`, borderRadius: 10,
  padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer",
};

const btnGreen = {
  backgroundColor: COLORS.greenBtn, color: "#fff",
  border: "none", borderRadius: 10, padding: "8px 20px",
  fontSize: 13, fontWeight: 700, cursor: "pointer",
};

export default function EAPWellnessIndex() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");
  const [answers, setAnswers] = useState({});
  const [currentDim, setCurrentDim] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [avgHistoryData, setAvgHistoryData] = useState([]);
  const [expandedRec, setExpandedRec] = useState(null);
  const [scores, setScores] = useState(null);
  const [orgStats, setOrgStats] = useState(null);
  const [orgStatsLoading, setOrgStatsLoading] = useState(false);
  const latestScores = scores || historyData[0] || {
    composite: 0,
    emotional: 0,
    mental: 0,
    physical: 0,
    social: 0,
    financial: 0
  };

  const handleFinish = async () => {
    if (!user?.id) {
      alert("User not found");
      return;
    }

    const res = await createAssessment(user, answers);

    if (res?.data) {
      const newData = res.data.data;

      setScores(newData);

      // ✅ Add this line
      setHistoryData(prev => [newData, ...prev]);

      // ✅ Recalculate avgHistoryData with new data
      setAvgHistoryData(prev => {
        const updatedHistory = [newData, ...historyData];
        const averages = {
          emotional: 0,
          mental: 0,
          physical: 0,
          social: 0,
          financial: 0
        };
        updatedHistory.forEach(h => {
          Object.keys(averages).forEach(key => {
            averages[key] += h[key] || 0;
          });
        });
        Object.keys(averages).forEach(key => {
          averages[key] = updatedHistory.length > 0 ? averages[key] / updatedHistory.length : 0;
        });
        return averages;
      });

      // ✅ Refetch organization stats after assessment submission
      const orgStatsRes = await getOrganizationStats();
      if (orgStatsRes?.data && orgStatsRes.data.data) {
        setOrgStats(orgStatsRes.data.data);
      }

      setView("results");
    } else {
      alert("Failed to submit assessment");
    }
  };

  console.log("avgHistoryData", avgHistoryData)

  useEffect(() => {
    const fetchHistory = async () => {
      const res = await getAssessmentHistory(user?.id);
      if (res?.data) {
        setHistoryData(res.data.data);
        setAvgHistoryData(res.data.averages)

      }
    };

    const fetchOrgStats = async () => {
      setOrgStatsLoading(true);
      const res = await getOrganizationStats();
      console.log('Organization stats response:', res);
      if (res?.data && res.data.data) {
        setOrgStats(res.data.data);
        console.log('Organization stats set:', res.data.data);
      } else {
        console.log('No organization stats data found');
      }
      setOrgStatsLoading(false);
    };

    // Always fetch organization stats on component mount
    fetchOrgStats();

    // Fetch history if user exists, or wait for user to be available
    if (user?.id) {
      fetchHistory();
    }
  }, []); // Empty dependency array to run only on mount

  // Separate effect to fetch history when user becomes available
  useEffect(() => {
    if (user?.id) {
      const fetchHistory = async () => {
        const res = await getAssessmentHistory(user?.id);
        if (res?.data) {
          setHistoryData(res.data.data);
          setAvgHistoryData(res.data.averages)
        }
      };
      fetchHistory();
    }
  }, [user?.id]);

  // Debug effect to monitor orgStats changes
  useEffect(() => {
    console.log('orgStats state changed:', orgStats);
  }, [orgStats]);


  // ═══ DASHBOARD ═══
  if (view === "dashboard") {
    // If user hasn't taken assessment, show prompt only
    if (historyData.length === 0) {
      return (
        <div style={{ minHeight: "100vh", backgroundColor: "#FFFFFF", fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif", paddingTop: "20px", position: "relative" }}>
          <div style={{ position: "absolute", right: 24, top: 24, zIndex: 1000 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                color: "#7843f3",
                textTransform: 'none',
                fontWeight: 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 14,
                padding: '8px 16px',
                borderRadius: 8,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
              Back
            </button>
          </div>
          <div style={{ maxWidth: 960, margin: "60px auto 0", padding: "0 24px" }}>
            <div style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 16,
              padding: "48px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid #E2E8F0",
            }}>
              <div style={{ marginBottom: 48, textAlign: "center" }}>
                <h1 style={{ fontSize: 36, fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: "0 0 16px" }}>
                  Wellness Index
                </h1>
                <p style={{ fontSize: 16, color: "#4A5568", lineHeight: 1.6, marginBottom: 24, maxWidth: 600, margin: "0 auto 24px" }}>
                  A comprehensive evaluation of your wellbeing across multiple dimensions to provide personalized health insights.
                </p>

              </div>

              <div style={{ marginBottom: 48, textAlign: "center" }}>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
                  {[
                    { key: "physical", label: "Physical", icon: "💪", desc: "Physical health and fitness" },
                    { key: "mental", label: "Mental", icon: "🧠", desc: "Mental clarity and focus" },
                    { key: "social", label: "Social", icon: "🤝", desc: "Social connections" },
                    { key: "emotional", label: "Emotional", icon: "💛", desc: "Emotional wellbeing" },
                    { key: "financial", label: "Financial", icon: "📊", desc: "Financial health" },
                  ].map((dim) => (
                    <div key={dim.key} style={{
                      backgroundColor: "#F7FAFC",
                      borderRadius: 12,
                      padding: 20,
                      border: "1px solid #E2E8F0",
                    }}>
                      <span style={{ fontSize: 32, display: "block", marginBottom: 12 }}>{dim.icon}</span>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1A202C", margin: "0 0 8px" }}>{dim.label}</h3>
                      <p style={{ fontSize: 13, color: "#718096", margin: 0, lineHeight: 1.5 }}>{dim.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  onClick={() => { setView("assess"); setCurrentDim(0); setAnswers({}); }}
                  style={{
                    background: "linear-gradient(135deg, #2ABFAB 0%, #6C63FF 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "16px 48px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    minWidth: 240,
                  }}
                >
                  Take Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    const recommendationSets =
  RECOMMENDATIONS?.[expandedRec]?.[
    getRecLevel(latestScores[expandedRec])
  ] || [];

const recommendations =
  recommendationSets[
    Math.floor(Math.random() * recommendationSets.length)
  ] || [];

    return (
      <div style={{ minHeight: "100vh", backgroundColor: COLORS.bg, fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif", paddingTop: "20px", position: "relative" }}>
        <div style={{ position: "absolute", right: 24, top: 24, zIndex: 1000 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              color: "#7843f3",
              textTransform: 'none',
              fontWeight: 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 14,
              padding: '8px 16px',
              borderRadius: 8,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 14 }} />
            Back
          </button>
        </div>
        <div style={{ background: "transparent", padding: "24px 0 20px" }}>
          <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div>
                <div style={{ fontSize: 28, fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Wellness Index</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>

              {historyData.length > 0 && (
                <button onClick={() => setView("history")} style={{ ...btnSecondary }}>📈 Trends</button>
              )}
              <div style={{ width: 10 }}></div>
              <button onClick={() => { setView("assess"); setCurrentDim(0); setAnswers({}); }} style={{ backgroundColor: "#fff", color: COLORS.primary, border: "none", borderRadius: 10, padding: "8px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Take Assessment</button>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 24px 48px" }}>
          <div style={{ ...cardStyle, marginBottom: 20, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 32 }}>
            <ScoreRing score={latestScores.composite} size={160} strokeWidth={12} delay={200} />
            <div style={{ flex: 1, minWidth: 200 }}>

              <h2
  style={{
    margin: "0 0 12px",
    fontSize: "1.8rem",
    fontWeight: 700,
    background: "linear-gradient(135deg,#22c55e 5%, #14b8a6 35%, #551cda 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    lineHeight: 1.2,
  }}
>
  Your Last Assessment Score
</h2>
              <p style={{ fontSize: 12,  color: "#000", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 4 }}>Composite Wellness Index</p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <TierBadge score={latestScores.composite} />
                <TrafficDot score={latestScores.composite} />
              </div>
              <p style={{ fontSize: 14, color: COLORS.textMid, lineHeight: 1.6, maxWidth: 380 }}>
                {latestScores.composite >= 81 ? "Outstanding wellness! You're thriving across all dimensions." :
                  latestScores.composite >= 61 ? "Good overall wellbeing with room for targeted improvements." :
                    latestScores.composite >= 41 ? "Developing wellness profile. Focus on lower-scoring areas." :
                      latestScores.composite >= 21 ? "Several areas need attention. EAP resources recommended." :
                        "Critical wellness score. Immediate support and intervention advised."}
              </p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
            {DIMENSIONS.map((d) => (
              <div key={d.key} onClick={() => setExpandedRec(expandedRec === d.key ? null : d.key)}
                style={{ ...cardStyle, padding: 16, cursor: "pointer", border: expandedRec === d.key ? `2px solid ${d.color}` : `1px solid ${COLORS.border}`, transition: "all 0.2s" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 24 }}>{d.icon}</span>
                  <TrafficDot score={latestScores[d.key]} />
                </div>
                <p style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{d.label}</p>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: COLORS.textDark }}>{latestScores[d.key]}</span>
                  <TierBadge score={latestScores[d.key]} small />
                </div>
                <ProgressBar score={latestScores[d.key]} color={d.color} />
                <p style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8 }}>Tap for recommendations →</p>
              </div>
            ))}
          </div>

          {expandedRec && (
            <div style={{ ...cardStyle, marginBottom: 16, border: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${DIMENSIONS.find(d => d.key === expandedRec)?.color}`, animation: "fadeIn 0.3s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 22 }}>{DIMENSIONS.find(d => d.key === expandedRec)?.icon}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.textDark }}>{DIMENSIONS.find(d => d.key === expandedRec)?.label} Wellness</span>
                <span style={{ fontSize: 13, color: COLORS.textLight, marginLeft: 4 }}>— Personalized Recommendations</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                {recommendations.map((rec, ri) => (
                  <div key={ri} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 14, borderRadius: 12, backgroundColor: DIMENSIONS.find(d => d.key === expandedRec)?.lightBg, border: `1px solid ${DIMENSIONS.find(d => d.key === expandedRec)?.borderColor}` }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: DIMENSIONS.find(d => d.key === expandedRec)?.color, backgroundColor: `${DIMENSIONS.find(d => d.key === expandedRec)?.color}18` }}>{ri + 1}</div>
                    <p style={{ fontSize: 13, color: COLORS.textMid, lineHeight: 1.5, margin: 0 }}>{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ ...cardStyle, padding: 18 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Wellness Index Scoring Key</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              {TIERS.map(t => (
                <span key={t.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 8, backgroundColor: t.bg, border: `1px solid ${t.border}` }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: t.color, display: "inline-block" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: t.color }}>{t.label}</span>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{t.min}–{t.max}</span>
                </span>
              ))}
              <span style={{ width: 1, height: 20, backgroundColor: COLORS.border, display: "inline-block", margin: "0 4px" }} />
              {[{ l: "Red", c: "#E53E3E" }, { l: "Amber", c: "#D69E2E" }, { l: "Green", c: "#38A169" }].map(x => (
                <span key={x.l} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: x.c, boxShadow: `0 0 6px ${x.c}50`, display: "inline-block" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: x.c }}>{x.l}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); }}`}</style>
      </div>
    );
  }

  // ═══ ASSESSMENT ═══
  if (view === "assess") {
    const dim = DIMENSIONS[currentDim];
    const qs = QUESTIONS[dim.key];
    const allDone = qs.every((_, i) => answers[`${dim.key}_${i}`] !== undefined);
    const prog = ((currentDim * 4 + Object.keys(answers).filter(k => k.startsWith(dim.key)).length) / 20) * 100;

    return (
      <div style={{ minHeight: "100vh", backgroundColor: COLORS.bg, fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif", paddingTop: "20px", position: "relative" }}>
        <div style={{ background: "transparent", padding: "24px 0 20px" }}>
          <div style={{ padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 28, fontWeight: 700, background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>EAP Wellness Index</div>
            <button
              onClick={() => setView("dashboard")}
              style={{
                color: "#7843f3",
                textTransform: 'none',
                fontWeight: 500,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 14,
                padding: '8px 16px',
                borderRadius: 8,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
              Back
            </button>
          </div>
        </div>
        {/* <div style={{ width: "100%", height: 4, backgroundColor: "#E2E8F0" }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg, #2ABFAB, #6C63FF)", width: `${prog}%`, transition: "width 0.4s ease", borderRadius: "0 4px 4px 0" }} />
        </div> */}
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <span style={{ fontSize: 48, display: "block", marginBottom: 12 }}>{dim.icon}</span>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: "0 0 4px" }}>{dim.label} Wellness</h2>
            <p style={{ fontSize: 13, color: COLORS.textLight }}>Rate each statement from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {DIMENSIONS.map((d, i) => (
                <div key={d.key} style={{ width: 32, height: 5, borderRadius: 4, backgroundColor: i === currentDim ? COLORS.primary : i < currentDim ? "#6C63FF" : "#E2E8F0", transition: "all 0.3s" }} />
              ))}
            </div>
          </div>
          {qs.map((q, qi) => {
            const key = `${dim.key}_${qi}`;
            return (
              <div key={qi} style={{ ...cardStyle, marginBottom: 12 }}>
                <p style={{ fontSize: 14, color: COLORS.textDark, lineHeight: 1.6, marginBottom: 14 }}>{q}</p>
                <div style={{ display: "flex", gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(v => (
                    <button key={v} onClick={() => setAnswers(p => ({ ...p, [key]: v }))}
                      style={{
                        flex: 1, padding: "10px 0", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
                        ...(answers[key] === v
                          ? { backgroundColor: dim.color, color: "#fff", border: "none", boxShadow: `0 3px 12px ${dim.color}35` }
                          : { backgroundColor: "#F7FAFC", color: COLORS.textMid, border: `1px solid ${COLORS.border}` }),
                      }}>{v}</button>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>Strongly Disagree</span>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>Strongly Agree</span>
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
            <button onClick={() => currentDim > 0 ? setCurrentDim(currentDim - 1) : setView("dashboard")}
              style={{ background: "none", border: "none", fontSize: 14, color: COLORS.textLight, cursor: "pointer", fontWeight: 600 }}>
              ← {currentDim > 0 ? "Previous" : "Dashboard"}
            </button>
            <button disabled={!allDone} onClick={() => currentDim < 4 ? setCurrentDim(currentDim + 1) : handleFinish()}
              style={{ ...btnPrimary, opacity: allDone ? 1 : 0.4, cursor: allDone ? "pointer" : "not-allowed", padding: "12px 32px" }}>
              {currentDim < 4 ? "Next Dimension →" : "View My Results ✨"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ RESULTS ═══
  if (view === "results") {
    const sorted = DIMENSIONS.map(d => ({ ...d, score: scores[d.key] })).sort((a, b) => a.score - b.score);
    return (
      <div style={{ minHeight: "100vh", backgroundColor: COLORS.bg, fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif", paddingTop: "20px", position: "relative" }}>
        <div style={{ position: "absolute", right: 24, top: 24, zIndex: 1000 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              color: "#7843f3",
              textTransform: 'none',
              fontWeight: 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 14,
              padding: '8px 16px',
              borderRadius: 8,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 14 }} />
            Back
          </button>
        </div>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "32px 24px 48px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: 0 }}>Your Wellness Results</h1>
              <p style={{ fontSize: 13, color: COLORS.textLight, margin: "4px 0 0" }}>Completed — {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {historyData.length > 0 && (
                <button onClick={() => setView("history")} style={{ ...btnSecondary }}>📈 Trends</button>
              )}
              <button onClick={() => setView("dashboard")} style={btnGreen}>Dashboard →</button>
            </div>
          </div>

          <div style={{ ...cardStyle, textAlign: "center", marginBottom: 20, paddingTop: 32, paddingBottom: 32, paddingLeft: 24, paddingRight: 24 }}>
            <p style={{ fontSize: 12, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12 }}>Composite Wellness Index</p>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              <ScoreRing score={scores.composite} size={180} strokeWidth={14} delay={300} />
            </div>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
              <TierBadge score={scores.composite} />
              <TrafficDot score={scores.composite} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 14, marginBottom: 20 }}>
            {DIMENSIONS.map((d, i) => (
              <div key={d.key} style={{ ...cardStyle, border: `1px solid ${COLORS.border}`, borderLeft: `4px solid ${d.color}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{d.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.textDark }}>{d.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <TierBadge score={scores[d.key]} small />
                    <TrafficDot score={scores[d.key]} />
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                  <ScoreRing score={scores[d.key]} size={72} strokeWidth={7} color={d.color} delay={400 + i * 120} />
                  <div style={{ flex: 1 }}>
                    <ProgressBar score={scores[d.key]} color={d.color} />
                    <p style={{ fontSize: 12, color: COLORS.textLight, marginTop: 6 }}>
                      {scores[d.key] >= 81 ? "Excellent — maintain your practices" :
                        scores[d.key] >= 61 ? "Good — minor improvements possible" :
                          scores[d.key] >= 41 ? "Fair — targeted support recommended" :
                            "Needs attention — EAP resources available"}
                    </p>
                  </div>
                </div>
                <div style={{ borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 12 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Recommended Actions</p>
                 {(
  RECOMMENDATIONS?.[d.key]?.[
    getRecLevel(scores[d.key])
  ]?.[0] || []
).map((rec, ri) => (
                    <div key={ri} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <span style={{ color: d.color, fontSize: 12, marginTop: 1 }}>●</span>
                      <span style={{ fontSize: 13, color: COLORS.textMid, lineHeight: 1.5 }}>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...cardStyle, background: "linear-gradient(135deg, #E6FAF7 0%, #F0EFFE 100%)", border: `1px solid ${COLORS.primary}30` }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textDark, marginBottom: 8 }}>🎯 Your Wellness Action Plan</h3>
            <p style={{ fontSize: 14, color: COLORS.textMid, lineHeight: 1.7, marginBottom: 16 }}>
              Based on your assessment, echoBuddy recommends focusing on your <strong style={{ color: sorted[0].color }}>{sorted[0].label.toLowerCase()}</strong> and <strong style={{ color: sorted[1].color }}>{sorted[1].label.toLowerCase()}</strong> wellness dimensions first. Your next assessment is recommended in 30 days.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setView("assess"); setCurrentDim(0); setAnswers({}); }} style={btnSecondary}>Retake Assessment</button>
              <button onClick={() => setView("dashboard")} style={btnPrimary}>Go to Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══ TRENDS ═══
  if (view === "history") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: COLORS.bg, fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif", paddingTop: "20px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", padding: "32px 24px 48px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: 0 }}>Wellness Trends</h1>
              <p style={{ fontSize: 13, color: COLORS.textLight, margin: "4px 0 0" }}>Track your wellness index over time</p>
            </div>
            <button onClick={() => setView("dashboard")} style={btnSecondary}>← Dashboard</button>
          </div>

          <div style={{ ...cardStyle, marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textDark, marginBottom: 20 }}>Composite Index Trend</h3>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 200, padding: "0 8px" }}>
              {historyData.map((d, i) => {
                const tier = getTier(d.composite);
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: tier.color }}>{d.composite}</span>
                    <div style={{ width: "100%", maxWidth: 48, borderRadius: "8px 8px 0 0", height: (d.composite / 100) * 170, backgroundColor: tier.color, opacity: 0.8, transition: "height 0.6s ease" }} />
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                      {(() => {
                        const date = new Date(d.created_at);
                        return !isNaN(date)
                          ? date.toLocaleDateString("en-US", { month: "short" })
                          : "-";
                      })()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 20 }}>
            {DIMENSIONS.map(d => (
              <div key={d.key} style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span>{d.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.textDark }}>{d.label}</span>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: d.color }}>
                    {avgHistoryData?.[d.key] != null
                      ? Math.round(avgHistoryData[d.key])
                      : 0}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 60 }}>
                  {historyData.map((h, i) => (
                    <div key={i} style={{ flex: 1, borderRadius: "4px 4px 0 0", height: `${(h[d.key] / 100) * 55}px`, backgroundColor: d.color, opacity: 0.4 + (i / historyData.length) * 0.6, transition: "height 0.5s ease" }} />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{historyData[0]?.month}</span>
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{historyData[historyData.length - 1]?.month}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...cardStyle, background: "linear-gradient(135deg, #E6FAF7 0%, #F0EFFE 100%)", border: `1px solid ${COLORS.primary}30` }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textDark, marginBottom: 16 }}>📊 Organization Insights</h3>
            {orgStatsLoading ? (
              <div style={{ textAlign: "center", padding: "20px", color: COLORS.textLight }}>
                Loading organization insights...
              </div>
            ) : orgStats ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
                {[
                  { label: "Avg Index", val: typeof orgStats.avgIndex === 'object' ? orgStats.avgIndex.value || 0 : orgStats.avgIndex || 0, sub: typeof orgStats.avgIndex === 'object' ? orgStats.avgIndex.trend || "No data" : "No data" },
                  { label: "Assessments", val: typeof orgStats.assessments === 'object' ? orgStats.assessments.total || 0 : 0, sub: typeof orgStats.assessments === 'object' ? orgStats.assessments.participation || "No data" : "No data" },
                  { label: "Top Area", val: typeof orgStats.topArea === 'object' ? orgStats.topArea.dimension || "N/A" : "N/A", sub: typeof orgStats.topArea === 'object' ? `Score: ${orgStats.topArea.score || 0}` : "Score: 0" },
                  { label: "Focus Area", val: typeof orgStats.focusArea === 'object' ? orgStats.focusArea.dimension || "N/A" : "N/A", sub: typeof orgStats.focusArea === 'object' ? `Score: ${orgStats.focusArea.score || 0}` : "Score: 0" },
                ].map((s, i) => (
                  <div key={i} style={{ backgroundColor: COLORS.card, borderRadius: 12, padding: 14, textAlign: "center", border: `1px solid ${COLORS.border}` }}>
                    <p style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{s.label}</p>
                    <p style={{ fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: 0 }}>{s.val}</p>
                    <p style={{ fontSize: 11, color: COLORS.textLight, margin: "4px 0 0" }}>{s.sub}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px", color: COLORS.textLight }}>
                Unable to load organization insights
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
